import { c as _c } from "react/compiler-runtime";
import { getHostedIdentifySessionRequirementsOptions, getHostedIdentifySessionRequirementsQueryKey, postHostedIdentifySessionChallengeQueryKey, postHostedIdentifySessionMutation, postHostedUserConsentMutation } from '@onefootprint/fetch-hosted';
import type { ChallengeKind, IdentifiedUser } from '@onefootprint/request-types';
import { useToast } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { DifferentAccountOption } from '@/idv2/features/identify/different-account-option';
import { clearIdentifyBack, pushIdentifyBack } from '@/idv2/features/identify/identify-back';
import { type ChallengeKindType, LoginChallengeSelect } from '@/idv2/features/identify/login-challenge-select';
import { TryAnotherWay } from '@/idv2/features/identify/try-another-way';
import { useTenantLogo } from '@/idv2/features/identify/use-tenant-logo';
import { getValidatedIdentifyBootstrap } from '@/idv2/features/identify/validate-bootstrap';
import { setOnboardingSessionToken } from '@/idv2/features/idv-to-idv2';
import { useFootprint } from '@/idv2/hooks/use-footprint';
import { isGetChallengeResponseError, runPasskeyChallenge } from '@/idv2/hooks/use-step-up/run-passkey-challenge';
import { checkDeviceInfo } from '@/idv2/utils/get-device';
import { getLogger } from '@/idv2/utils/logger';
const {
  logError
} = getLogger({
  name: 'identify-login'
});
type LoginLoaderData = {
  loginRequirement: {
    user: IdentifiedUser;
  };
  methods: ChallengeKindType[];
  showDifferentAccount: boolean;
  showTryAnotherWay: boolean;
  displayTermsAndConditions: boolean;
};
export const Route = createFileRoute('/identify/login')({
  component: LoginPage,
  loader: async ({
    context
  }): Promise<LoginLoaderData> => {
    const headers = {
      'X-Fp-Authorization': context.onboardingSessionToken
    };
    const [requirementsData, device] = await Promise.all([context.queryClient.ensureQueryData(getHostedIdentifySessionRequirementsOptions({
      headers
    })), checkDeviceInfo()]);
    const loginRequirement = requirementsData?.requirements?.[0]?.kind === 'login' ? requirementsData.requirements[0] : undefined;
    if (!loginRequirement) {
      throw new Error('Reached /identify/login without a login requirement in the identify session');
    }
    const kinds = loginRequirement.user?.availableChallengeKinds ?? [];
    const otpMethods = kinds.filter(k => k === 'sms' || k === 'email');
    const canShowBiometric = kinds.includes('biometric') && device.hasSupportForWebauthn && (device.type === 'mobile' || !!loginRequirement.user?.hasSyncablePasskey);
    const methods: ChallengeKindType[] = canShowBiometric ? ['biometric', ...otpMethods] : otpMethods;
    const {
      isBootstrap
    } = getValidatedIdentifyBootstrap(context.kycBootstrapData);
    const playbookHidesOption = context.config?.flags?.shouldHideBootstrappedLoginWithDifferent ?? false;
    const isStaticMatch = loginRequirement.user.isStaticMatch ?? false;
    const isComponentsSdk = context.isComponentsSdk ?? false;
    const showDifferentAccount = isBootstrap && !playbookHidesOption && !isStaticMatch && !isComponentsSdk;
    const hasUnverifiedEmailAuth = loginRequirement.user?.authMethods?.some(m => m.kind === 'email' && m.isVerified === false);
    const showTryAnotherWay = !!hasUnverifiedEmailAuth && !loginRequirement.user?.isUnverified;
    const displayTermsAndConditions = requirementsData?.displayTermsAndConditions ?? false;
    return {
      loginRequirement,
      methods,
      showDifferentAccount,
      showTryAnotherWay,
      displayTermsAndConditions
    };
  }
});
function LoginPage() {
  const $0 = _c(58);
  const tenantLogo = useTenantLogo();
  const navigate = useNavigate();
  const footprint = useFootprint();
  const {
    toast
  } = useToast();
  const {
    t
  } = useTranslation("idv2");
  const {
    config,
    onboardingSessionToken,
    obConfigAuth,
    queryClient,
    sandboxId: contextSandboxId,
    isComponentsSdk,
    scope,
    afterIdentify
  } = Route.useRouteContext();
  const {
    loginRequirement,
    methods,
    showDifferentAccount,
    showTryAnotherWay,
    displayTermsAndConditions
  } = Route.useLoaderData();
  let t0;
  if ($0[0] !== onboardingSessionToken) {
    t0 = {
      mutationFn: () => runPasskeyChallenge(onboardingSessionToken)
    };
    $0[0] = onboardingSessionToken;
    $0[1] = t0;
  } else {
    t0 = $0[1];
  }
  const passkeyMutation = useMutation(t0);
  let t1;
  if ($0[2] !== onboardingSessionToken) {
    t1 = postHostedUserConsentMutation({
      headers: {
        "X-Fp-Authorization": onboardingSessionToken
      }
    });
    $0[2] = onboardingSessionToken;
    $0[3] = t1;
  } else {
    t1 = $0[3];
  }
  const consentMutation = useMutation(t1);
  let t2;
  if ($0[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = postHostedIdentifySessionMutation();
    $0[4] = t2;
  } else {
    t2 = $0[4];
  }
  const resetSessionMutation = useMutation(t2);
  let t3;
  if ($0[5] !== consentMutation || $0[6] !== displayTermsAndConditions || $0[7] !== footprint || $0[8] !== loginRequirement.user.scrubbedEmail || $0[9] !== loginRequirement.user.scrubbedPhone || $0[10] !== navigate || $0[11] !== onboardingSessionToken || $0[12] !== passkeyMutation || $0[13] !== queryClient || $0[14] !== t || $0[15] !== toast) {
    t3 = async challengeKind => {
      if (challengeKind === "biometric") {
        ;
        try {
          await passkeyMutation.mutateAsync();
          if (displayTermsAndConditions) {
            await consentMutation.mutateAsync({
              body: {
                content: t(_temp),
                kind: "terms_and_conditions"
              }
            });
          }
          clearIdentifyBack();
          footprint.navigation.forward();
        } catch (t4) {
          const error = t4;
          const isPasskeyError = isGetChallengeResponseError(error);
          toast({
            title: t($ => isPasskeyError ? $.identify.login.passkeyError.title : $.errorFallback.genericError.title),
            description: t($_0 => isPasskeyError ? $_0.identify.login.passkeyError.description : $_0.errorFallback.genericError.subtitle),
            variant: "error"
          });
        }
        return;
      }
      const otpKind = challengeKind === "sms" || challengeKind === "email" ? challengeKind : "sms";
      queryClient.removeQueries({
        queryKey: postHostedIdentifySessionChallengeQueryKey({
          headers: {
            "X-Fp-Authorization": onboardingSessionToken
          },
          body: {
            challengeKind: otpKind === "sms" ? "email" : "sms"
          }
        })
      });
      pushIdentifyBack({
        to: "/identify/login"
      });
      navigate({
        to: "/identify/otp/$challengeKind",
        params: {
          challengeKind: otpKind
        },
        search: {
          scrubbedEmail: loginRequirement.user?.scrubbedEmail,
          scrubbedPhone: loginRequirement.user?.scrubbedPhone,
          fromChallengeSelector: true
        }
      });
    };
    $0[5] = consentMutation;
    $0[6] = displayTermsAndConditions;
    $0[7] = footprint;
    $0[8] = loginRequirement.user.scrubbedEmail;
    $0[9] = loginRequirement.user.scrubbedPhone;
    $0[10] = navigate;
    $0[11] = onboardingSessionToken;
    $0[12] = passkeyMutation;
    $0[13] = queryClient;
    $0[14] = t;
    $0[15] = toast;
    $0[16] = t3;
  } else {
    t3 = $0[16];
  }
  const handleSelect = t3;
  let t4;
  if ($0[17] !== config?.isLive || $0[18] !== contextSandboxId || $0[19] !== isComponentsSdk || $0[20] !== navigate || $0[21] !== obConfigAuth || $0[22] !== onboardingSessionToken || $0[23] !== queryClient || $0[24] !== resetSessionMutation || $0[25] !== scope) {
    t4 = async () => {
      if (!obConfigAuth) {
        logError("handleReset called without obConfigAuth");
        return;
      }
      const isLive = config?.isLive ?? true;
      const sandboxId = !isLive ? contextSandboxId : undefined;
      const data = await resetSessionMutation.mutateAsync({
        body: {
          data: {},
          scope
        },
        headers: {
          ...obConfigAuth,
          ...(sandboxId ? {
            "X-Sandbox-Id": sandboxId
          } : {}),
          ...(isComponentsSdk === true ? {
            "X-Fp-Is-Components-Sdk": true
          } : {})
        }
      });
      queryClient.invalidateQueries({
        queryKey: getHostedIdentifySessionRequirementsQueryKey({
          headers: {
            "X-Fp-Authorization": onboardingSessionToken
          }
        })
      });
      setOnboardingSessionToken(data.token);
      clearIdentifyBack();
      navigate({
        to: "/identify"
      });
    };
    $0[17] = config?.isLive;
    $0[18] = contextSandboxId;
    $0[19] = isComponentsSdk;
    $0[20] = navigate;
    $0[21] = obConfigAuth;
    $0[22] = onboardingSessionToken;
    $0[23] = queryClient;
    $0[24] = resetSessionMutation;
    $0[25] = scope;
    $0[26] = t4;
  } else {
    t4 = $0[26];
  }
  const handleReset = t4;
  let t5;
  if ($0[27] !== navigate) {
    t5 = () => {
      pushIdentifyBack({
        to: "/identify/login"
      });
      navigate({
        to: "/identify/kba"
      });
    };
    $0[27] = navigate;
    $0[28] = t5;
  } else {
    t5 = $0[28];
  }
  const handleTryAnotherWay = t5;
  const shouldShowWelcomeBack = !loginRequirement.user.isUnverified;
  let t6;
  if ($0[29] !== shouldShowWelcomeBack || $0[30] !== t) {
    t6 = shouldShowWelcomeBack ? t(_temp2) : t(_temp3);
    $0[29] = shouldShowWelcomeBack;
    $0[30] = t;
    $0[31] = t6;
  } else {
    t6 = $0[31];
  }
  const title = t6;
  let t7;
  if ($0[32] !== afterIdentify) {
    t7 = $_4 => afterIdentify === "/auth-methods" ? $_4.identify.login.logInToModifyDetails : $_4.identify.login.logInOptions;
    $0[32] = afterIdentify;
    $0[33] = t7;
  } else {
    t7 = $0[33];
  }
  let t8;
  if ($0[34] !== t || $0[35] !== t7) {
    t8 = t(t7);
    $0[34] = t;
    $0[35] = t7;
    $0[36] = t8;
  } else {
    t8 = $0[36];
  }
  let t9;
  if ($0[37] !== t) {
    t9 = t(_temp4);
    $0[37] = t;
    $0[38] = t9;
  } else {
    t9 = $0[38];
  }
  const t10 = passkeyMutation.isPending || consentMutation.isPending;
  let t11;
  if ($0[39] !== handleSelect || $0[40] !== loginRequirement || $0[41] !== methods || $0[42] !== t10 || $0[43] !== t8 || $0[44] !== t9 || $0[45] !== tenantLogo || $0[46] !== title) {
    t11 = <LoginChallengeSelect headerIcon={tenantLogo} loginRequirement={loginRequirement} methods={methods} onSelect={handleSelect} title={title} subtitle={t8} continueLabel={t9} isLoading={t10} />;
    $0[39] = handleSelect;
    $0[40] = loginRequirement;
    $0[41] = methods;
    $0[42] = t10;
    $0[43] = t8;
    $0[44] = t9;
    $0[45] = tenantLogo;
    $0[46] = title;
    $0[47] = t11;
  } else {
    t11 = $0[47];
  }
  let t12;
  if ($0[48] !== handleTryAnotherWay || $0[49] !== showTryAnotherWay) {
    t12 = showTryAnotherWay && <TryAnotherWay onClick={handleTryAnotherWay} />;
    $0[48] = handleTryAnotherWay;
    $0[49] = showTryAnotherWay;
    $0[50] = t12;
  } else {
    t12 = $0[50];
  }
  let t13;
  if ($0[51] !== handleReset || $0[52] !== showDifferentAccount) {
    t13 = showDifferentAccount && <DifferentAccountOption onReset={handleReset} />;
    $0[51] = handleReset;
    $0[52] = showDifferentAccount;
    $0[53] = t13;
  } else {
    t13 = $0[53];
  }
  let t14;
  if ($0[54] !== t11 || $0[55] !== t12 || $0[56] !== t13) {
    t14 = <>{t11}{t12}{t13}</>;
    $0[54] = t11;
    $0[55] = t12;
    $0[56] = t13;
    $0[57] = t14;
  } else {
    t14 = $0[57];
  }
  return t14;
}
function _temp4($_5) {
  return $_5.identify.login.continue;
}
function _temp3($_3) {
  return $_3.identify.login.verifyTitle;
}
function _temp2($_2) {
  return $_2.identify.login.welcomeBackTitle;
}
function _temp($_1) {
  return $_1.identify.otp.legalFooter;
}